import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MessageDraftRevisionNotice } from '@/features/messaging/MessageDraftRevisionNotice';
import { RecipientAutocompleteInput } from '@/features/messaging/RecipientAutocompleteInput';
import type { DraftFields, MessageDraftRevision } from '@/features/messaging/useMessageDraftLifecycle';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

interface MessageComposeFieldsProps {
  fields: DraftFields;
  disabled: boolean;
  onChange: (patch: Partial<DraftFields>) => void;
  onBlur: () => void;
  onSend: () => void;
  canSend: boolean;
  isSending: boolean;
  error: string | null;
  revision: MessageDraftRevision;
  bodyRows?: number;
}

export function MessageComposeFields({
  fields,
  disabled,
  onChange,
  onBlur,
  onSend,
  canSend,
  isSending,
  error,
  revision,
  bodyRows = 8,
}: MessageComposeFieldsProps) {
  const [showCcBcc, setShowCcBcc] = useState(fields.cc.length > 0 || fields.bcc.length > 0);

  return (
    <>
      <div className="space-y-2 px-3 py-2" onBlur={onBlur}>
        <div className="flex items-center gap-2">
          <RecipientAutocompleteInput
            value={fields.to}
            onChange={(value) => onChange({ to: value })}
            placeholder="To"
            ariaLabel="To"
            disabled={disabled}
          />
          <button
            type="button"
            onClick={() => setShowCcBcc((value) => !value)}
            className="shrink-0 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            Cc Bcc
          </button>
        </div>
        {showCcBcc ? (
          <>
            <RecipientAutocompleteInput
              value={fields.cc}
              onChange={(value) => onChange({ cc: value })}
              placeholder="Cc"
              ariaLabel="Cc"
              disabled={disabled}
            />
            <RecipientAutocompleteInput
              value={fields.bcc}
              onChange={(value) => onChange({ bcc: value })}
              placeholder="Bcc"
              ariaLabel="Bcc"
              disabled={disabled}
            />
          </>
        ) : null}
        <Input
          value={fields.subject}
          onChange={(event) => onChange({ subject: event.target.value })}
          placeholder="Subject"
          aria-label="Subject"
          disabled={disabled}
        />
        <Textarea
          value={fields.authoredBody}
          onChange={(event) => onChange({ authoredBody: event.target.value })}
          placeholder="Write your message"
          aria-label="Message body"
          rows={bodyRows}
          disabled={disabled}
          className="resize-none"
        />
      </div>

      <MessageDraftRevisionNotice revision={revision} />

      {error ? (
        <p className="px-3 pb-1 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-2 border-t px-3 py-2">
        <Button type="button" onClick={onSend} disabled={!canSend}>
          {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Send
        </Button>
      </div>
    </>
  );
}
